import {
    AlertTriangle,
    Bot,
    ChevronLeft,
    ChevronRight,
    CircleHelp,
    ExternalLink,
    Loader2,
    Mail,
    MessageSquare,
    Phone,
    RefreshCw,
    UserCog,
} from 'lucide-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { BlockedPhonePopover } from '@apps/web/components/caregivers/blocked_phone_popover';
import { CaregiverAvailabilityProfile } from '@apps/web/components/caregivers/caregiver_availability_profile';
import { CaregiverCallTimelineTab } from '@apps/web/components/caregivers/caregiver_call_timeline_tab';
import { CaregiverComplianceBadge } from '@apps/web/components/caregivers/caregiver_compliance_badge';
import { CaregiverConversationTimelineTab } from '@apps/web/components/caregivers/caregiver_conversation_timeline_tab';
import { CaregiverOverview } from '@apps/web/components/caregivers/caregiver_overview';
import { CaregiverPointsTab } from '@apps/web/components/caregivers/caregiver_points_tab';
import { CaregiverSchedulingProfile } from '@apps/web/components/caregivers/caregiver_scheduling_profile';
import { HHAExchangeCaregiverStatusBadge } from '@apps/web/components/caregivers/hhaexchange_caregiver_status_badge';
import { RoleBadge } from '@apps/web/components/caregivers/role_badge';
import { ProfileAvatar } from '@apps/web/components/entity_profile/profile_avatar';
import { ProfileShell } from '@apps/web/components/entity_profile/profile_shell';
import { EntityEventsTimeline } from '@apps/web/components/events/entity_events_timeline';
import { EntityPreferencesSection } from '@apps/web/components/memory/entity_preferences_section';
import { MemoryTab } from '@apps/web/components/memory/memory_tab';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { CallDetailDialog } from '@apps/web/components/voice_calls/call_detail_dialog';
import { useResourceOrganizationLookup } from '@apps/web/hooks/use_resource_organization_lookup';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { formatCaregiverName } from '@apps/web/lib/caregiver_name';
import { isCaregiverPointsEnabled } from '@apps/web/lib/caregiver_points';
import {
    generateExternalCaregiverUrl,
    getDataSourceDisplayName,
} from '@apps/web/lib/data_source_utils';
import {
    formatDateAndTime,
    formatDateKey,
    shiftDateKey,
    toDateKey,
} from '@apps/web/lib/date_utils';
import {
    getApiErrorDetail,
    isTransparentRateLimitError,
} from '@apps/web/lib/known_api_errors';
import {
    getLanguageName,
    normalizeSupportedLanguage,
    normalizeTextLanguage,
    SUPPORTED_LANGUAGES,
    VOICE_FALLBACK_LANGUAGES,
    VOICE_UNSUPPORTED_LANGUAGES,
} from '@apps/web/lib/languages';
import {
    useBlockedCaregivers,
    useCaregiverPointEntries,
    useOrganization,
    useOrganizationCaregiver,
    useOrganizationCaregiverShiftOutcomes,
    useOrganizationDataSourceAccounts,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { ApiError } from '@libraries/typescript/api_client';
import { type components } from '@libraries/typescript/api_client/generated_api_types';
import { assert } from '@libraries/typescript/utilities';

// Nav display order for the profile sections; the JSX below is grouped by
// data locality, so the array is sorted into this order before rendering.
const CAREGIVER_SECTION_ORDER = [
    'overview',
    'availability',
    'details',
    'preferences',
    'calls',
    'messages',
    'outcomes',
    'points',
    'memory',
    'activity',
];

const ISO_639_1_TO_DISPLAY_NAME: Record<string, string> = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    ru: 'Russian',
    ar: 'Arabic',
    ne: 'Nepali',
    zh: 'Chinese',
    ht: 'Haitian Creole',
    pt: 'Portuguese',
    bn: 'Bengali',
    hi: 'Hindi',
    ur: 'Urdu',
    pa: 'Punjabi',
    ko: 'Korean',
    vi: 'Vietnamese',
    pl: 'Polish',
    it: 'Italian',
    de: 'German',
    tl: 'Tagalog',
    el: 'Greek',
    he: 'Hebrew',
    ja: 'Japanese',
    th: 'Thai',
    tr: 'Turkish',
    uk: 'Ukrainian',
    ro: 'Romanian',
    fa: 'Persian',
    sq: 'Albanian',
    sr: 'Serbian',
    hr: 'Croatian',
    yi: 'Yiddish',
    sw: 'Swahili',
    am: 'Amharic',
    so: 'Somali',
    gu: 'Gujarati',
    ta: 'Tamil',
    te: 'Telugu',
    ml: 'Malayalam',
    mr: 'Marathi',
    my: 'Burmese',
    km: 'Khmer',
    id: 'Indonesian',
    ms: 'Malay',
};

const LANGUAGE_OPTIONS = Object.entries(ISO_639_1_TO_DISPLAY_NAME)
    .map(([code, name]) => ({
        value: code,
        label: name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

function formatLanguageDisplay(language: string): string {
    const displayName =
        ISO_639_1_TO_DISPLAY_NAME[language] ?? getLanguageName(language);
    return displayName !== language ? `${displayName} (${language})` : language;
}

// Display name of the provider language used for a voice-unsupported
// (text-only) primary language, e.g. "French" for Haitian Creole.
function voiceFallbackDisplayName(language: string): string {
    const fallbackCode = VOICE_FALLBACK_LANGUAGES[language] ?? language;
    return ISO_639_1_TO_DISPLAY_NAME[fallbackCode] ?? fallbackCode;
}

function supportedVoiceFallbackDisplayName(
    primaryLanguage: string,
    supportedLanguages: Array<string>,
): string | null {
    // Mirrors resolve_caregiver_voice_language in libraries/python:
    // normalize codes/names first, prefer English, then another listed
    // language that voice vendors can speak directly.
    const normalizedLanguages = supportedLanguages
        .map(normalizeSupportedLanguage)
        .filter((language): language is string => language !== null);
    if (normalizedLanguages.includes('en')) {
        return SUPPORTED_LANGUAGES['en'] ?? 'English';
    }

    const fallbackCode = normalizedLanguages.find(
        (language) =>
            language !== primaryLanguage &&
            !(language in VOICE_FALLBACK_LANGUAGES),
    );
    if (!fallbackCode) {
        return null;
    }

    return SUPPORTED_LANGUAGES[fallbackCode] ?? fallbackCode;
}

const OUTCOME_PAGE_SIZE = 50;

// Mirrors the server-side single-caregiver sync throttle
// (`_SINGLE_CAREGIVER_SYNC_RATE_LIMIT_WINDOW_S`): after a successful sync we
// disable the button for this long so re-clicks are no-ops instead of 429s.
const SINGLE_CAREGIVER_SYNC_COOLDOWN_S = 30;

type CaregiverShiftOutcomeSource =
    components['schemas']['CaregiverShiftOutcomeSource'];
type CaregiverShiftOutcomeType =
    components['schemas']['CaregiverShiftOutcomeType'];
type CaregiverShiftOutcomeRead =
    components['schemas']['CaregiverShiftOutcomeRead'];
type CaregiverShiftOutcomesResponse =
    components['schemas']['CaregiverShiftOutcomesResponse'];

function defaultOutcomeDateRange(timezone: string): {
    readonly startDate: string;
    readonly endDate: string;
} {
    const endDate = toDateKey(new Date(), timezone);
    return {
        startDate: shiftDateKey(endDate, -90),
        endDate,
    };
}

function OutcomeStatTile({
    label,
    value,
}: {
    readonly label: string;
    readonly value: number | undefined;
}) {
    return (
        <div className="rounded-md border p-3">
            <div className="text-muted-foreground text-xs">{label}</div>
            <div className="mt-1 text-2xl font-semibold">{value ?? '—'}</div>
        </div>
    );
}

function formatOutcomeSource(source: CaregiverShiftOutcomeSource): string {
    switch (source) {
        case 'inbound_voice_cancellation':
            return 'Inbound voice';
        case 'inbound_sms_cancellation':
            return 'Inbound SMS';
        case 'shift_confirmation_response':
            return 'Shift confirmation';
        case 'manual_api':
            return 'Manual';
        case 'phoebe_agent':
            return 'Phoebe';
        case 'callout_response':
            return 'Shift offer';
        case 'ehr_sync':
            return 'EHR sync';
        default:
            assert.never(source);
    }
}

function formatOutcomeType(outcomeType: CaregiverShiftOutcomeType): string {
    switch (outcomeType) {
        case 'cancellation':
            return 'Cancellation';
        case 'decline':
            return 'Shift confirmation decline';
        case 'shift_offer_decline':
            return 'Shift offer decline';
        default:
            assert.never(outcomeType);
    }
}

// Drive attribution off the outcome's source enum, not off which back-link
// FKs happen to be populated. Phoebe declines also persist actor_user_id
// (the coordinator running the agent) for analytics, so gating purely on
// actor_name presence mislabels agent triage as a manual action.
function renderOutcomeAttribution(
    outcome: CaregiverShiftOutcomeRead,
): React.ReactNode {
    switch (outcome.latest_source) {
        case 'phoebe_agent':
            return (
                <span className="text-muted-foreground inline-flex items-center gap-1">
                    <Bot className="h-3 w-3" />
                    Triaged by Phoebe agent
                </span>
            );
        case 'manual_api':
            return outcome.actor_name ? (
                <span className="text-muted-foreground inline-flex items-center gap-1">
                    <UserCog className="h-3 w-3" />
                    Manual by {outcome.actor_name}
                </span>
            ) : null;
        case 'inbound_voice_cancellation':
        case 'inbound_sms_cancellation':
        case 'shift_confirmation_response':
        case 'callout_response':
        case 'ehr_sync':
            return null;
        default:
            assert.never(outcome.latest_source);
    }
}

function formatVoiceCallFallback(voiceCall: {
    readonly direction: string;
    readonly call_status: string;
    readonly duration_ms: number | null;
}): string {
    const directionLabel =
        voiceCall.direction === 'inbound' ? 'Inbound call' : 'Voice call';
    if (voiceCall.duration_ms === null) {
        return `${directionLabel} ${voiceCall.call_status.replace(/_/g, ' ')}`;
    }
    const minutes = Math.floor(voiceCall.duration_ms / 60000);
    const seconds = Math.floor((voiceCall.duration_ms % 60000) / 1000);
    return `${directionLabel} lasted ${minutes}:${String(seconds).padStart(2, '0')}`;
}

type CaregiverDetailModalProps = {
    readonly isOpen: boolean;
    readonly onClose: () => void;
    readonly onNavigateAdjacent?: (direction: 'previous' | 'next') => void;
    readonly organizationId: string;
    readonly caregiverId: string | undefined;
};

function shouldIgnoreArrowNavigation(
    target: EventTarget | null,
    profileDialog: HTMLElement | null,
): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    return (
        (profileDialog !== null && !profileDialog.contains(target)) ||
        target.isContentEditable ||
        target.closest(
            'input, textarea, select, [contenteditable="true"], [role="combobox"], [role="listbox"], [role="menu"], [role="slider"], [role="spinbutton"]',
        ) !== null
    );
}

export function CaregiverDetailModal({
    isOpen,
    onClose,
    onNavigateAdjacent,
    organizationId,
    caregiverId,
}: CaregiverDetailModalProps) {
    const profileDialogRef = useRef<HTMLDivElement>(null);
    const {
        data: caregiver,
        error,
        isLoading,
        mutate,
    } = useOrganizationCaregiver(organizationId, caregiverId || null, {
        reason: 'caregiver-detail-modal',
    });
    const isMissingCaregiverInCurrentOrganization =
        error instanceof ApiError && error.status === 404;
    const { isResolvingAcrossOrganizations } = useResourceOrganizationLookup({
        enabled: !!caregiverId && isMissingCaregiverInCurrentOrganization,
        resourceId: caregiverId,
        resourceType: 'caregiver',
    });

    const { data: organization } = useOrganization(organizationId, {
        reason: 'caregiver-detail-modal',
    });
    const caregiverPointsEnabled = isCaregiverPointsEnabled(
        organization?.features,
    );
    const caregiverPreferenceProfilesEnabled =
        organization?.features?.['caregiver_preference_profiles_enabled'] ===
        true;
    const { data: pointEntriesData, isLoading: isLoadingPointEntries } =
        useCaregiverPointEntries(
            organizationId,
            caregiverPointsEnabled ? caregiverId || null : null,
            { reason: 'caregiver-detail-modal-points' },
            { limit: 50 },
        );

    const [primaryLanguage, setPrimaryLanguage] = useState<string | null>(null);
    const [supportedLanguages, setSupportedLanguages] = useState<Array<string>>(
        [],
    );
    const initialOutcomeTimezone = organization?.timezone ?? 'UTC';
    const defaultedOutcomeTimezoneRef = useRef(initialOutcomeTimezone);
    const [outcomeStartDate, setOutcomeStartDate] = useState(
        () => defaultOutcomeDateRange(initialOutcomeTimezone).startDate,
    );
    const [outcomeEndDate, setOutcomeEndDate] = useState(
        () => defaultOutcomeDateRange(initialOutcomeTimezone).endDate,
    );
    const userTouchedOutcomeDatesRef = useRef(false);
    const [outcomeOffset, setOutcomeOffset] = useState(0);
    const [selectedOutcomeVoiceCallId, setSelectedOutcomeVoiceCallId] =
        useState<string | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncCooldownUntilMs, setSyncCooldownUntilMs] = useState<
        number | null
    >(null);
    const [syncCooldownSecondsRemaining, setSyncCooldownSecondsRemaining] =
        useState(0);
    // Tracks the currently-shown caregiver so an in-flight sync that
    // resolves after the modal switches caregivers does not apply its
    // cooldown/toast to the wrong one.
    const currentCaregiverIdRef = useRef(caregiverId);
    useEffect(() => {
        currentCaregiverIdRef.current = caregiverId;
    }, [caregiverId]);

    useEffect(() => {
        if (!isOpen || !onNavigateAdjacent) {
            return undefined;
        }

        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.defaultPrevented ||
                event.altKey ||
                event.ctrlKey ||
                event.metaKey ||
                event.shiftKey ||
                shouldIgnoreArrowNavigation(
                    event.target,
                    profileDialogRef.current,
                )
            ) {
                return;
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                onNavigateAdjacent('previous');
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                onNavigateAdjacent('next');
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onNavigateAdjacent]);
    const { data: dataSourceAccounts } = useOrganizationDataSourceAccounts(
        organizationId,
        { reason: 'caregiver-detail-modal' },
    );
    const {
        data: shiftOutcomes,
        isLoading: isLoadingShiftOutcomes,
        error: shiftOutcomesError,
    } = useOrganizationCaregiverShiftOutcomes(
        organizationId,
        caregiverId || null,
        outcomeStartDate,
        outcomeEndDate,
        OUTCOME_PAGE_SIZE,
        outcomeOffset,
        { reason: 'caregiver-detail-modal' },
    );

    // Counts do not change across pagination. Keep the last loaded response
    // for this caregiver and date range so page turns do not blank the tiles,
    // while the identity key prevents another caregiver's counts leaking in.
    const outcomeCountsKey = `${organizationId}|${caregiverId ?? ''}|${outcomeStartDate}|${outcomeEndDate}`;
    const lastLoadedOutcomeCountsRef = useRef<{
        key: string;
        counts: CaregiverShiftOutcomesResponse;
    } | null>(null);
    useEffect(() => {
        if (shiftOutcomes && !shiftOutcomesError) {
            lastLoadedOutcomeCountsRef.current = {
                key: outcomeCountsKey,
                counts: shiftOutcomes,
            };
        }
    }, [shiftOutcomes, shiftOutcomesError, outcomeCountsKey]);
    const heldOutcomeCounts =
        lastLoadedOutcomeCountsRef.current?.key === outcomeCountsKey
            ? lastLoadedOutcomeCountsRef.current.counts
            : undefined;
    const outcomeCounts = shiftOutcomesError
        ? undefined
        : (shiftOutcomes ?? heldOutcomeCounts);

    // Check if this caregiver's phone is blocked
    const caregiverIdsForBlockedCheck = useMemo(
        () => (caregiverId ? [caregiverId] : null),
        [caregiverId],
    );

    const { data: blockedCaregivers, mutate: mutateBlockedCaregivers } =
        useBlockedCaregivers(organizationId, caregiverIdsForBlockedCheck);

    const blockedCaregiverInfos = useMemo(
        () =>
            blockedCaregivers?.filter((b) => b.caregiver_id === caregiverId) ??
            [],
        [blockedCaregivers, caregiverId],
    );

    const [activeSectionKey, setActiveSectionKey] = useState('overview');
    // The modal component stays mounted across open/close cycles, so
    // re-land on Overview whenever it (re)opens or switches caregivers.
    useEffect(() => {
        if (isOpen) {
            setActiveSectionKey('overview');
        }
    }, [isOpen, caregiverId]);

    // Update local state when caregiver data loads
    useEffect(() => {
        if (
            !organization?.timezone ||
            defaultedOutcomeTimezoneRef.current === organization.timezone ||
            userTouchedOutcomeDatesRef.current
        ) {
            return;
        }

        const defaultRange = defaultOutcomeDateRange(organization.timezone);
        setOutcomeStartDate(defaultRange.startDate);
        setOutcomeEndDate(defaultRange.endDate);
        defaultedOutcomeTimezoneRef.current = organization.timezone;
    }, [organization?.timezone]);

    useEffect(() => {
        setOutcomeOffset(0);
    }, [caregiverId, outcomeStartDate, outcomeEndDate]);

    useEffect(() => {
        if (caregiver?.primary_language !== undefined) {
            setPrimaryLanguage(caregiver.primary_language || null);
        }
    }, [caregiver?.primary_language]);

    useEffect(() => {
        if (caregiver?.supported_languages !== undefined) {
            setSupportedLanguages(caregiver.supported_languages || []);
        }
    }, [caregiver?.supported_languages]);

    const sourceLanguageDivergenceTooltip = useMemo(() => {
        if (
            caregiver?.source_system === undefined ||
            caregiver.source_system === null ||
            caregiver.source_supported_languages === undefined ||
            caregiver.source_supported_languages === null
        ) {
            return null;
        }

        const sourceSystemDisplayName = getDataSourceDisplayName(
            caregiver.source_system,
        );
        const sourceSupportedLanguages = caregiver.source_supported_languages;
        const sourcePrimaryLanguage = caregiver.source_primary_language ?? null;
        const savedPrimaryLanguage = caregiver.primary_language ?? null;
        const savedSupportedLanguages = caregiver.supported_languages ?? [];
        const sourceLanguageSet = new Set([
            ...sourceSupportedLanguages,
            ...(sourcePrimaryLanguage ? [sourcePrimaryLanguage] : []),
        ]);
        const phoebeLanguageSet = new Set([
            ...savedSupportedLanguages,
            ...(savedPrimaryLanguage ? [savedPrimaryLanguage] : []),
        ]);
        const hasPrimaryMismatch =
            sourcePrimaryLanguage !== null &&
            sourcePrimaryLanguage !== savedPrimaryLanguage;
        const hasPhoebeOnlyLanguage = Array.from(phoebeLanguageSet).some(
            (language) => !sourceLanguageSet.has(language),
        );
        const hasWellSkyOnlyLanguage = Array.from(sourceLanguageSet).some(
            (language) => !phoebeLanguageSet.has(language),
        );

        if (
            !hasPrimaryMismatch &&
            !hasPhoebeOnlyLanguage &&
            !hasWellSkyOnlyLanguage
        ) {
            return null;
        }

        const sourceLanguages =
            sourceSupportedLanguages.length > 0
                ? sourceSupportedLanguages.map(formatLanguageDisplay).join(', ')
                : 'None';
        const phoebeLanguages =
            phoebeLanguageSet.size > 0
                ? Array.from(phoebeLanguageSet)
                      .map(formatLanguageDisplay)
                      .join(', ')
                : 'None';
        const sourcePrimaryLanguageDisplay = sourcePrimaryLanguage
            ? formatLanguageDisplay(sourcePrimaryLanguage)
            : 'None';
        const phoebePrimaryLanguage = savedPrimaryLanguage
            ? formatLanguageDisplay(savedPrimaryLanguage)
            : 'None';

        return {
            phoebePrimaryLanguage,
            sourceLanguages,
            sourcePrimaryLanguage: sourcePrimaryLanguageDisplay,
            sourceSystemDisplayName,
            phoebeLanguages,
        };
    }, [
        caregiver?.source_system,
        caregiver?.source_primary_language,
        caregiver?.source_supported_languages,
        caregiver?.primary_language,
        caregiver?.supported_languages,
    ]);

    // The server rate limit is per caregiver, so a cooldown from one
    // caregiver must not disable Sync when the modal switches to another.
    useEffect(() => {
        setSyncCooldownUntilMs(null);
    }, [caregiverId]);

    useEffect(() => {
        if (syncCooldownUntilMs === null) {
            setSyncCooldownSecondsRemaining(0);
            return;
        }
        const tick = () => {
            const remainingS = Math.ceil(
                (syncCooldownUntilMs - Date.now()) / 1000,
            );
            setSyncCooldownSecondsRemaining(Math.max(remainingS, 0));
            return remainingS <= 0;
        };
        if (tick()) {
            return;
        }
        const interval = setInterval(() => {
            if (tick()) {
                clearInterval(interval);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [syncCooldownUntilMs]);

    const handleSave = useCallback(async () => {
        if (!organizationId || !caregiverId) {
            return;
        }

        setIsSaving(true);
        try {
            await getApiClient().patch({
                url: '/organizations/{organization_id}/caregivers/{caregiver_id}',
                pathParams: {
                    organization_id: organizationId,
                    caregiver_id: caregiverId,
                },
                body: {
                    primary_language: primaryLanguage,
                    supported_languages: supportedLanguages,
                },
            });

            toast.success('Caregiver settings updated successfully.', {
                persistNotification: false,
            });
            setHasChanges(false);
            mutate();
        } catch (error) {
            if (!isTransparentRateLimitError(error)) {
                toast.error('Failed to update caregiver settings.', {
                    persistNotification: true,
                    notificationType: 'form_failure',
                });
            }
        } finally {
            setIsSaving(false);
        }
    }, [
        organizationId,
        caregiverId,
        primaryLanguage,
        supportedLanguages,
        mutate,
    ]);

    const handleSyncCaregiver = useCallback(async () => {
        if (!organizationId || !caregiverId) {
            return;
        }

        // Capture the caregiver this sync is for; the modal may switch
        // caregivers before the request resolves.
        const syncCaregiverId = caregiverId;
        setIsSyncing(true);
        try {
            await getApiClient().post({
                url: '/organizations/{organization_id}/caregivers/{caregiver_id}/sync',
                pathParams: {
                    organization_id: organizationId,
                    caregiver_id: syncCaregiverId,
                },
            });
        } catch (syncError) {
            if (currentCaregiverIdRef.current !== syncCaregiverId) {
                return;
            }
            if (isTransparentRateLimitError(syncError)) {
                return;
            }
            const guardrailDetail =
                syncError instanceof ApiError && syncError.status === 429
                    ? getApiErrorDetail(syncError)
                    : null;
            if (guardrailDetail !== null) {
                toast.error(guardrailDetail, {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                });
            } else {
                toast.error('Failed to sync caregiver. Please try again.', {
                    persistNotification: true,
                    notificationType: 'data_sync_action',
                });
            }
            return;
        } finally {
            setIsSyncing(false);
        }

        // The sync succeeded — but if the modal switched caregivers while it
        // was in flight, don't apply this caregiver's cooldown/toast to
        // whoever is shown now.
        if (currentCaregiverIdRef.current !== syncCaregiverId) {
            return;
        }
        // Arm the cooldown (the server burned the rate-limit budget on the
        // 200) and confirm to the user. The data refetch below is a
        // best-effort UI refresh — a failure there is not a sync failure, so
        // it must not surface the error toast.
        setSyncCooldownUntilMs(
            Date.now() + SINGLE_CAREGIVER_SYNC_COOLDOWN_S * 1000,
        );
        toast.success('Caregiver synced', {
            persistNotification: true,
            notificationType: 'data_sync_action',
        });
        try {
            await mutate();
        } catch {
            // The sync already succeeded server-side; SWR will revalidate the
            // stale caregiver data on its own.
        }
    }, [organizationId, caregiverId, mutate]);

    const arraysEqual = (a: Array<string>, b: Array<string>) =>
        a.length === b.length && a.every((v, i) => v === b[i]);

    const checkForChanges = useCallback(
        (newPrimaryLang: string | null, newSupportedLangs: Array<string>) => {
            const primaryChanged =
                newPrimaryLang !== (caregiver?.primary_language || null);
            const supportedChanged = !arraysEqual(
                newSupportedLangs,
                caregiver?.supported_languages || [],
            );
            setHasChanges(primaryChanged || supportedChanged);
        },
        [caregiver],
    );

    const handlePrimaryLanguageChange = (value: string) => {
        const newValue = value === 'none' ? null : value;
        setPrimaryLanguage(newValue);
        checkForChanges(newValue, supportedLanguages);
    };

    const handleSupportedLanguagesChange = (value: Array<string>) => {
        setSupportedLanguages(value);
        checkForChanges(primaryLanguage, value);
    };

    const displayCaregiverNamesLastFirst =
        organization?.general_settings?.display_caregiver_names_last_first ??
        false;
    const caregiverTags = caregiver?.caregiver_tags ?? [];
    const caregiverStarRating = caregiver?.star_rating ?? null;
    const organizationTimezone = organization?.timezone ?? 'UTC';
    const complianceEvaluationDate = toDateKey(
        new Date(),
        organizationTimezone,
    );
    const knownComplianceRequirements = (caregiver?.compliance ?? []).flatMap(
        (requirement) =>
            requirement.expires_on
                ? [{ ...requirement, expires_on: requirement.expires_on }]
                : [],
    );
    const externalAvailabilitySourceLabel = caregiver?.source_system
        ? getDataSourceDisplayName(caregiver.source_system)
        : undefined;
    const clientPreferenceRows = (caregiver?.caregiver_client_preferences ?? [])
        .filter((preference) => preference.preference_value !== 'no_preference')
        .map((preference) => ({
            id: preference.id,
            name: preference.client_name,
            preference_value: preference.preference_value,
            origin: preference.origin,
            source: preference.source,
        }));
    const outcomePageStart =
        shiftOutcomes && shiftOutcomes.total_count > 0
            ? shiftOutcomes.offset + 1
            : 0;
    const outcomePageEnd = shiftOutcomes
        ? Math.min(
              shiftOutcomes.offset + shiftOutcomes.outcomes.length,
              shiftOutcomes.total_count,
          )
        : 0;
    // Language warnings key off the normalized code so name-form values
    // from EHR syncs ("Haitian Creole") behave like their ISO codes.
    const normalizedPrimaryLanguage = primaryLanguage
        ? normalizeSupportedLanguage(primaryLanguage)
        : null;
    // SMS supports any language the LLM handles, so resolve against the broad
    // text table rather than the voice-speakable subset.
    const textPrimaryLanguage = primaryLanguage
        ? normalizeTextLanguage(primaryLanguage)
        : null;
    // Voice can speak a language only when it is in the voice-supported subset
    // and not one of the text-only fallback languages.
    const isVoiceSpeakable =
        normalizedPrimaryLanguage !== null &&
        !VOICE_UNSUPPORTED_LANGUAGES.has(normalizedPrimaryLanguage);
    // Text-only: Phoebe can text in this language but voice cannot speak it.
    const isTextOnlyLanguage =
        textPrimaryLanguage !== null && !isVoiceSpeakable;
    const textLanguageName = textPrimaryLanguage
        ? getLanguageName(textPrimaryLanguage)
        : primaryLanguage;
    // True only for the fallback-map languages (e.g. Haitian Creole) whose
    // voice calls keep the caregiver's language but borrow another language's
    // speech settings. Other text-only languages fall back fully to a spoken
    // language (English or another listed language).
    const usesVoiceSpeechFallback =
        normalizedPrimaryLanguage !== null &&
        VOICE_UNSUPPORTED_LANGUAGES.has(normalizedPrimaryLanguage);
    const supportedVoiceFallbackName = isTextOnlyLanguage
        ? supportedVoiceFallbackDisplayName(
              textPrimaryLanguage ?? '',
              supportedLanguages,
          )
        : null;

    const shellContent =
        caregiver && caregiverId ? (
            <ProfileShell
                header={null}
                activeSectionKey={activeSectionKey}
                onSectionChange={setActiveSectionKey}
                sections={[
                    {
                        key: 'overview',
                        label: 'Overview',
                        content: (
                            <CaregiverOverview
                                organizationId={organizationId}
                                caregiverId={caregiverId}
                                caregiverFirstName={
                                    caregiver.first_name ?? null
                                }
                                timezone={organization?.timezone ?? 'UTC'}
                                externalSourceLabel={
                                    externalAvailabilitySourceLabel
                                }
                            />
                        ),
                    },
                    {
                        key: 'memory',
                        label: 'Memory',
                        content: (
                            <MemoryTab
                                organizationId={organizationId}
                                entityId={caregiverId}
                                entityType="caregiver"
                            />
                        ),
                    },
                    {
                        key: 'availability',
                        label: 'Availability',
                        content: (
                            <div className="space-y-4">
                                {caregiverPreferenceProfilesEnabled && (
                                    <CaregiverSchedulingProfile
                                        organizationId={organizationId}
                                        caregiverId={caregiverId ?? null}
                                    />
                                )}
                                <CaregiverAvailabilityProfile
                                    organizationId={organizationId}
                                    caregiverId={caregiverId}
                                    externalSourceLabel={
                                        externalAvailabilitySourceLabel
                                    }
                                />
                            </div>
                        ),
                    },
                    {
                        key: 'calls',
                        label: 'Calls',
                        content: (
                            <CaregiverCallTimelineTab
                                organizationId={organizationId}
                                caregiverId={caregiverId}
                                organizationTimezone={
                                    organization?.timezone ?? null
                                }
                                onNavigateToOutreach={onClose}
                            />
                        ),
                    },
                    {
                        key: 'messages',
                        label: 'Messages',
                        content: (
                            <CaregiverConversationTimelineTab
                                organizationId={organizationId}
                                caregiverId={caregiverId}
                                organizationTimezone={
                                    organization?.timezone ?? null
                                }
                                onNavigateToConversation={onClose}
                            />
                        ),
                    },
                    {
                        key: 'outcomes',
                        label: 'Outcomes',
                        content: (
                            <div className="space-y-4 p-1">
                                <div className="grid grid-cols-2 gap-2">
                                    <label className="space-y-1 text-xs">
                                        <span className="text-muted-foreground">
                                            Start
                                        </span>
                                        <Input
                                            type="date"
                                            value={outcomeStartDate}
                                            max={outcomeEndDate}
                                            onChange={(event) => {
                                                userTouchedOutcomeDatesRef.current = true;
                                                setOutcomeStartDate(
                                                    event.target.value,
                                                );
                                            }}
                                        />
                                    </label>
                                    <label className="space-y-1 text-xs">
                                        <span className="text-muted-foreground">
                                            End
                                        </span>
                                        <Input
                                            type="date"
                                            value={outcomeEndDate}
                                            min={outcomeStartDate}
                                            onChange={(event) => {
                                                userTouchedOutcomeDatesRef.current = true;
                                                setOutcomeEndDate(
                                                    event.target.value,
                                                );
                                            }}
                                        />
                                    </label>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <OutcomeStatTile
                                        label="Total"
                                        value={outcomeCounts?.total_count}
                                    />
                                    <OutcomeStatTile
                                        label="Cancellations"
                                        value={
                                            outcomeCounts?.cancellation_count
                                        }
                                    />
                                    <OutcomeStatTile
                                        label="Shift offer declines"
                                        value={
                                            outcomeCounts?.shift_offer_decline_count
                                        }
                                    />
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    <OutcomeStatTile
                                        label="Shifts offered"
                                        value={
                                            outcomeCounts?.shifts_offered_count
                                        }
                                    />
                                    <OutcomeStatTile
                                        label="Expressed interest"
                                        value={
                                            outcomeCounts?.interested_shift_count
                                        }
                                    />
                                    <OutcomeStatTile
                                        label="No response"
                                        value={
                                            outcomeCounts?.no_response_shift_count
                                        }
                                    />
                                </div>
                                {isLoadingShiftOutcomes ? (
                                    <div className="text-muted-foreground flex items-center gap-2 p-3 text-xs">
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Loading outcomes…
                                    </div>
                                ) : shiftOutcomesError ? (
                                    <div className="p-3 text-xs text-red-600">
                                        Failed to load outcomes.
                                    </div>
                                ) : shiftOutcomes &&
                                  shiftOutcomes.outcomes.length > 0 ? (
                                    <div className="space-y-2">
                                        {shiftOutcomes.outcomes.map(
                                            (outcome) => {
                                                const attribution =
                                                    renderOutcomeAttribution(
                                                        outcome,
                                                    );
                                                return (
                                                    <div
                                                        key={outcome.id}
                                                        className="rounded-md border p-3"
                                                    >
                                                        <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-medium break-words">
                                                                    {
                                                                        outcome.patient_name
                                                                    }
                                                                </div>
                                                                <div className="text-muted-foreground mt-1 text-xs">
                                                                    {formatDateAndTime(
                                                                        outcome.shift_start_time,
                                                                        organization?.timezone ??
                                                                            'UTC',
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-none flex-wrap justify-end gap-1">
                                                                <Badge variant="secondary">
                                                                    {formatOutcomeType(
                                                                        outcome.outcome_type,
                                                                    )}
                                                                </Badge>
                                                                <Badge variant="outline">
                                                                    {formatOutcomeSource(
                                                                        outcome.latest_source,
                                                                    )}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                        <div className="text-muted-foreground mt-2 text-xs">
                                                            Recorded{' '}
                                                            {formatDateAndTime(
                                                                outcome.occurred_at,
                                                                organization?.timezone ??
                                                                    'UTC',
                                                            )}
                                                        </div>
                                                        {outcome.reason_label ? (
                                                            <div className="mt-2 text-xs">
                                                                {
                                                                    outcome.reason_label
                                                                }
                                                            </div>
                                                        ) : null}
                                                        {outcome.conversation_id ||
                                                        attribution ? (
                                                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                                                {outcome.conversation_id ? (
                                                                    <a
                                                                        href={`/conversations?conversationId=${outcome.conversation_id}`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 underline"
                                                                    >
                                                                        <MessageSquare className="h-3 w-3" />
                                                                        View
                                                                        conversation
                                                                    </a>
                                                                ) : null}
                                                                {attribution}
                                                            </div>
                                                        ) : null}
                                                        {outcome.voice_call ? (
                                                            <div className="bg-muted/50 mt-3 space-y-2 rounded-md p-2">
                                                                <div className="text-muted-foreground text-xs">
                                                                    {outcome
                                                                        .voice_call
                                                                        .call_summary ??
                                                                        outcome
                                                                            .voice_call
                                                                            .transcript_preview ??
                                                                        formatVoiceCallFallback(
                                                                            outcome.voice_call,
                                                                        )}
                                                                </div>
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="h-7 px-2 text-xs"
                                                                    onClick={() =>
                                                                        setSelectedOutcomeVoiceCallId(
                                                                            outcome
                                                                                .voice_call
                                                                                ?.id ??
                                                                                null,
                                                                        )
                                                                    }
                                                                >
                                                                    View call
                                                                </Button>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                );
                                            },
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-muted-foreground rounded-md border p-3 text-xs">
                                        No outcomes in this range.
                                    </div>
                                )}
                                {shiftOutcomes &&
                                shiftOutcomes.total_count > 0 ? (
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-muted-foreground text-xs">
                                            Showing {outcomePageStart}-
                                            {outcomePageEnd} of{' '}
                                            {shiftOutcomes.total_count}
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                disabled={
                                                    isLoadingShiftOutcomes ||
                                                    shiftOutcomes.offset === 0
                                                }
                                                onClick={() =>
                                                    setOutcomeOffset(
                                                        (currentOffset) =>
                                                            Math.max(
                                                                0,
                                                                currentOffset -
                                                                    OUTCOME_PAGE_SIZE,
                                                            ),
                                                    )
                                                }
                                            >
                                                <ChevronLeft className="h-4 w-4" />
                                                Previous
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                disabled={
                                                    isLoadingShiftOutcomes ||
                                                    !shiftOutcomes.has_more
                                                }
                                                onClick={() =>
                                                    setOutcomeOffset(
                                                        (currentOffset) =>
                                                            currentOffset +
                                                            OUTCOME_PAGE_SIZE,
                                                    )
                                                }
                                            >
                                                Next
                                                <ChevronRight className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ),
                    },
                    ...(caregiverPointsEnabled
                        ? [
                              {
                                  key: 'points',
                                  label: 'Points',
                                  content: (
                                      <CaregiverPointsTab
                                          data={pointEntriesData}
                                          isLoading={isLoadingPointEntries}
                                          timezone={
                                              organization?.timezone ?? 'UTC'
                                          }
                                      />
                                  ),
                              },
                          ]
                        : []),
                    {
                        key: 'activity',
                        label: 'Activity',
                        content: (
                            <EntityEventsTimeline
                                organizationId={organizationId}
                                entityType="caregiver"
                                entityId={caregiverId}
                            />
                        ),
                    },
                    {
                        key: 'details',
                        label: 'Details',
                        content: (
                            <div className="max-w-4xl space-y-4">
                                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                    {caregiver.roles &&
                                        caregiver.roles.length > 0 && (
                                            <div className="flex min-w-0 items-center gap-2">
                                                <span className="text-muted-foreground text-sm">
                                                    Roles
                                                </span>
                                                <div className="flex flex-wrap gap-1">
                                                    {caregiver.roles.map(
                                                        (role) => (
                                                            <RoleBadge
                                                                key={role}
                                                                role={role}
                                                                className="text-xs"
                                                            />
                                                        ),
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    {caregiver.source_system === 'wellsky' && (
                                        <div
                                            className="flex items-center gap-2"
                                            data-testid="caregiver-rating-detail"
                                        >
                                            <span className="text-muted-foreground text-sm">
                                                Rating
                                            </span>
                                            <span className="text-sm">
                                                {caregiverStarRating ?? '—'}
                                            </span>
                                        </div>
                                    )}
                                    {caregiver.hhaexchange_caregiver_status && (
                                        <div
                                            className="flex items-center gap-2"
                                            data-testid="caregiver-status-detail"
                                        >
                                            <span className="text-muted-foreground text-sm">
                                                Status
                                            </span>
                                            <HHAExchangeCaregiverStatusBadge
                                                status={
                                                    caregiver.hhaexchange_caregiver_status
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                                <section className="space-y-1.5">
                                    <div className="flex items-center gap-1">
                                        <h3 className="text-xs font-semibold">
                                            Communication
                                        </h3>
                                        <Tooltip>
                                            <TooltipTrigger asChild={true}>
                                                <button
                                                    type="button"
                                                    className="text-muted-foreground hover:text-foreground inline-flex"
                                                    aria-label="About communication languages"
                                                >
                                                    <CircleHelp className="size-3" />
                                                </button>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                Languages used for calls and
                                                messages.
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                    <div className="grid items-end gap-2 sm:grid-cols-[minmax(8rem,11rem)_minmax(0,1fr)_auto]">
                                        <div className="min-w-0">
                                            <div className="text-muted-foreground mb-1 flex h-4 items-center gap-1 text-[10px] leading-none">
                                                Primary
                                                {isTextOnlyLanguage && (
                                                    <Tooltip>
                                                        <TooltipTrigger
                                                            asChild={true}
                                                        >
                                                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                                        </TooltipTrigger>
                                                        <TooltipContent className="max-w-[240px]">
                                                            Texts are sent in{' '}
                                                            {textLanguageName}
                                                            {usesVoiceSpeechFallback &&
                                                            !supportedVoiceFallbackName &&
                                                            normalizedPrimaryLanguage ? (
                                                                <React.Fragment>
                                                                    . Phone
                                                                    calls use{' '}
                                                                    {
                                                                        textLanguageName
                                                                    }{' '}
                                                                    with{' '}
                                                                    {voiceFallbackDisplayName(
                                                                        normalizedPrimaryLanguage,
                                                                    )}{' '}
                                                                    speech
                                                                    settings,
                                                                    since{' '}
                                                                    {
                                                                        textLanguageName
                                                                    }{' '}
                                                                    is not
                                                                    supported
                                                                    directly for
                                                                    voice yet.
                                                                </React.Fragment>
                                                            ) : (
                                                                <React.Fragment>
                                                                    . Phone
                                                                    calls use{' '}
                                                                    {supportedVoiceFallbackName ??
                                                                        'English'}
                                                                    , since{' '}
                                                                    {
                                                                        textLanguageName
                                                                    }{' '}
                                                                    is not
                                                                    supported
                                                                    for voice
                                                                    yet.
                                                                </React.Fragment>
                                                            )}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                                {primaryLanguage &&
                                                    !textPrimaryLanguage && (
                                                        <Tooltip>
                                                            <TooltipTrigger
                                                                asChild={true}
                                                            >
                                                                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-[200px]">
                                                                Communication
                                                                will be done in
                                                                English as their
                                                                primary language
                                                                is not
                                                                recognized.
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                {sourceLanguageDivergenceTooltip && (
                                                    <Tooltip>
                                                        <TooltipTrigger
                                                            asChild={true}
                                                        >
                                                            <AlertTriangle
                                                                aria-label={`Language differs from ${sourceLanguageDivergenceTooltip.sourceSystemDisplayName}`}
                                                                className="h-3.5 w-3.5 text-amber-500"
                                                            />
                                                        </TooltipTrigger>
                                                        <TooltipContent className="max-w-[260px]">
                                                            <div className="space-y-1">
                                                                <p>
                                                                    Phoebe
                                                                    language
                                                                    settings
                                                                    differ from{' '}
                                                                    {
                                                                        sourceLanguageDivergenceTooltip.sourceSystemDisplayName
                                                                    }
                                                                    .
                                                                </p>
                                                                <p>
                                                                    {
                                                                        sourceLanguageDivergenceTooltip.sourceSystemDisplayName
                                                                    }{' '}
                                                                    primary:{' '}
                                                                    {
                                                                        sourceLanguageDivergenceTooltip.sourcePrimaryLanguage
                                                                    }
                                                                </p>
                                                                <p>
                                                                    {
                                                                        sourceLanguageDivergenceTooltip.sourceSystemDisplayName
                                                                    }{' '}
                                                                    languages:{' '}
                                                                    {
                                                                        sourceLanguageDivergenceTooltip.sourceLanguages
                                                                    }
                                                                </p>
                                                                <p>
                                                                    Phoebe
                                                                    primary:{' '}
                                                                    {
                                                                        sourceLanguageDivergenceTooltip.phoebePrimaryLanguage
                                                                    }
                                                                </p>
                                                                <p>
                                                                    Phoebe
                                                                    languages:{' '}
                                                                    {
                                                                        sourceLanguageDivergenceTooltip.phoebeLanguages
                                                                    }
                                                                </p>
                                                            </div>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </div>
                                            <Select
                                                value={
                                                    primaryLanguage || 'none'
                                                }
                                                onValueChange={
                                                    handlePrimaryLanguageChange
                                                }
                                            >
                                                <SelectTrigger
                                                    size="sm"
                                                    className="h-8 w-full text-xs"
                                                >
                                                    <SelectValue placeholder="None" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-[min(250px,var(--radix-select-content-available-height))]">
                                                    <SelectItem value="none">
                                                        None
                                                    </SelectItem>
                                                    {LANGUAGE_OPTIONS.map(
                                                        (option) => (
                                                            <SelectItem
                                                                key={
                                                                    option.value
                                                                }
                                                                value={
                                                                    option.value
                                                                }
                                                            >
                                                                {option.label}
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-muted-foreground mb-1 flex h-4 items-center text-[10px] leading-none">
                                                Supported
                                            </div>
                                            <MultiSelect
                                                options={LANGUAGE_OPTIONS}
                                                selected={supportedLanguages}
                                                onChange={
                                                    handleSupportedLanguagesChange
                                                }
                                                placeholder="Select languages…"
                                                portalWithinDialog={true}
                                                className="min-h-8 px-2.5 py-0 text-xs font-normal"
                                            />
                                        </div>
                                        <Button
                                            size="sm"
                                            className="h-8 px-2.5 text-xs"
                                            onClick={handleSave}
                                            disabled={isSaving || !hasChanges}
                                        >
                                            {isSaving ? (
                                                <React.Fragment>
                                                    <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                                                    Saving…
                                                </React.Fragment>
                                            ) : (
                                                'Save'
                                            )}
                                        </Button>
                                    </div>
                                </section>
                                {knownComplianceRequirements.length > 0 ? (
                                    <section
                                        className="space-y-1.5 border-t pt-4"
                                        data-testid="caregiver-compliance-detail"
                                    >
                                        <h3 className="text-sm font-semibold">
                                            Compliance
                                        </h3>
                                        <div className="divide-y rounded-md border">
                                            {knownComplianceRequirements.map(
                                                (requirement) => {
                                                    const isCompliant =
                                                        requirement.expires_on >=
                                                        complianceEvaluationDate;

                                                    return (
                                                        <div
                                                            key={
                                                                requirement.external_id
                                                            }
                                                            className="flex items-center justify-between gap-3 px-3 py-2"
                                                        >
                                                            <div className="min-w-0">
                                                                <div className="text-sm font-medium break-words">
                                                                    {
                                                                        requirement.name
                                                                    }
                                                                </div>
                                                                <div className="text-muted-foreground text-xs">
                                                                    Expires{' '}
                                                                    {formatDateKey(
                                                                        requirement.expires_on,
                                                                        organizationTimezone,
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <span
                                                                className={
                                                                    isCompliant
                                                                        ? 'shrink-0 text-xs font-medium text-green-700'
                                                                        : 'shrink-0 text-xs font-medium text-red-700'
                                                                }
                                                            >
                                                                {isCompliant
                                                                    ? 'Compliant'
                                                                    : 'Non-compliant'}
                                                            </span>
                                                        </div>
                                                    );
                                                },
                                            )}
                                        </div>
                                    </section>
                                ) : null}
                                <div className="grid gap-4 border-t pt-4 sm:grid-cols-2">
                                    {(caregiver.address_line_1 ||
                                        caregiver.city ||
                                        caregiver.state ||
                                        caregiver.postal_code) && (
                                        <div>
                                            <h3 className="mb-1.5 text-sm font-semibold">
                                                Address
                                            </h3>
                                            <div className="space-y-0.5 text-sm">
                                                {caregiver.address_line_1 && (
                                                    <div>
                                                        {
                                                            caregiver.address_line_1
                                                        }
                                                    </div>
                                                )}
                                                {caregiver.address_line_2 && (
                                                    <div>
                                                        {
                                                            caregiver.address_line_2
                                                        }
                                                    </div>
                                                )}
                                                {(caregiver.city ||
                                                    caregiver.state ||
                                                    caregiver.postal_code) && (
                                                    <div>
                                                        {caregiver.city &&
                                                            caregiver.city}
                                                        {caregiver.city &&
                                                            caregiver.state &&
                                                            ', '}
                                                        {caregiver.state &&
                                                            caregiver.state}{' '}
                                                        {caregiver.postal_code &&
                                                            caregiver.postal_code}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {caregiver.contacts &&
                                        caregiver.contacts.length > 0 && (
                                            <div>
                                                <h3 className="mb-1.5 text-sm font-semibold">
                                                    Contact
                                                </h3>
                                                <div className="space-y-1.5">
                                                    {caregiver.contacts.map(
                                                        (contact, index) => {
                                                            const ContactIcon =
                                                                contact.type ===
                                                                'email'
                                                                    ? Mail
                                                                    : contact.type ===
                                                                        'phone'
                                                                      ? Phone
                                                                      : null;
                                                            return (
                                                                <div
                                                                    key={index}
                                                                    className="flex min-w-0 items-center gap-2"
                                                                >
                                                                    {ContactIcon ? (
                                                                        <ContactIcon
                                                                            className="text-muted-foreground size-3.5 shrink-0"
                                                                            aria-label={
                                                                                contact.type
                                                                            }
                                                                        />
                                                                    ) : (
                                                                        <span className="text-muted-foreground w-12 shrink-0 text-xs capitalize">
                                                                            {
                                                                                contact.type
                                                                            }
                                                                        </span>
                                                                    )}
                                                                    <span className="min-w-0 truncate text-sm">
                                                                        {
                                                                            contact.address
                                                                        }
                                                                    </span>
                                                                    {contact.is_primary && (
                                                                        <Badge
                                                                            variant="outline"
                                                                            className="text-xs"
                                                                        >
                                                                            Primary
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            );
                                                        },
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                </div>
                            </div>
                        ),
                    },
                    {
                        key: 'preferences',
                        label: 'Preferences',
                        content: (
                            <div className="max-w-4xl space-y-4">
                                {clientPreferenceRows.length > 0 ? (
                                    <EntityPreferencesSection
                                        title="Client Preferences"
                                        rows={clientPreferenceRows}
                                        importedSourceLabel={
                                            externalAvailabilitySourceLabel
                                        }
                                    />
                                ) : (
                                    <p className="text-muted-foreground text-sm">
                                        No client preferences recorded yet.
                                    </p>
                                )}
                            </div>
                        ),
                    },
                ].sort(
                    (a, b) =>
                        CAREGIVER_SECTION_ORDER.indexOf(a.key) -
                        CAREGIVER_SECTION_ORDER.indexOf(b.key),
                )}
            />
        ) : null;

    return (
        <React.Fragment>
            <Dialog open={isOpen} onOpenChange={onClose}>
                {/* ph-no-capture: this dialog portals outside any page's
                    ph-no-capture root and shows caregiver PII (name, tags,
                    contact info), so it must opt out on its own. */}
                <DialogContent
                    ref={profileDialogRef}
                    className="ph-no-capture flex h-[90dvh] max-h-[90dvh] w-full max-w-none min-w-0 flex-col gap-0 overflow-hidden p-0 sm:h-[min(600px,calc(100dvh-3rem))] sm:max-h-[calc(100dvh-3rem)] sm:w-[min(1180px,calc(100vw-3rem))] sm:max-w-none lg:min-w-[900px]"
                >
                    {isLoading || isResolvingAcrossOrganizations ? (
                        <div className="flex h-64 items-center justify-center">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : caregiver ? (
                        <React.Fragment>
                            <DialogHeader className="shrink-0 border-b px-4 py-3 sm:px-5">
                                <div className="flex min-w-0 flex-col gap-2 pr-8 sm:flex-row sm:items-center sm:justify-between">
                                    <div className="min-w-0 space-y-1.5">
                                        <DialogTitle className="flex min-w-0 flex-wrap items-center gap-2 text-base leading-tight sm:text-lg">
                                            <ProfileAvatar
                                                entityId={caregiver.id}
                                                displayName={formatCaregiverName(
                                                    caregiver.name,
                                                    displayCaregiverNamesLastFirst,
                                                )}
                                            />
                                            <span>
                                                {formatCaregiverName(
                                                    caregiver.name,
                                                    displayCaregiverNamesLastFirst,
                                                )}
                                            </span>
                                            {blockedCaregiverInfos.length >
                                                0 && (
                                                <BlockedPhonePopover
                                                    blockedInfos={
                                                        blockedCaregiverInfos
                                                    }
                                                    onUnblocked={() =>
                                                        mutateBlockedCaregivers()
                                                    }
                                                />
                                            )}
                                            {caregiver.roles?.map((role) => (
                                                <RoleBadge
                                                    key={role}
                                                    role={role}
                                                    className="text-xs"
                                                />
                                            ))}
                                            {caregiver.hhaexchange_caregiver_status && (
                                                <HHAExchangeCaregiverStatusBadge
                                                    status={
                                                        caregiver.hhaexchange_caregiver_status
                                                    }
                                                />
                                            )}
                                            {caregiver.caregiver_compliance ? (
                                                <CaregiverComplianceBadge
                                                    compliance={
                                                        caregiver.caregiver_compliance
                                                    }
                                                />
                                            ) : null}
                                        </DialogTitle>
                                        {caregiverTags.length > 0 && (
                                            <TooltipProvider
                                                delayDuration={150}
                                            >
                                                <div className="flex flex-wrap items-center gap-2">
                                                    {caregiverTags.map(
                                                        (tag) => (
                                                            <Tooltip
                                                                key={tag.key}
                                                            >
                                                                <TooltipTrigger
                                                                    asChild={
                                                                        true
                                                                    }
                                                                >
                                                                    <Badge
                                                                        variant={
                                                                            tag.variant ===
                                                                            'negative'
                                                                                ? 'destructive'
                                                                                : tag.variant ===
                                                                                    'positive'
                                                                                  ? 'success'
                                                                                  : 'secondary'
                                                                        }
                                                                        className="cursor-default"
                                                                    >
                                                                        {
                                                                            tag.label
                                                                        }
                                                                    </Badge>
                                                                </TooltipTrigger>
                                                                <TooltipContent className="whitespace-pre-line">
                                                                    {
                                                                        tag.tooltip
                                                                    }
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        ),
                                                    )}
                                                </div>
                                            </TooltipProvider>
                                        )}
                                    </div>
                                    {caregiver.external_id &&
                                        caregiver.source_system &&
                                        (() => {
                                            const dataSourceAccount =
                                                dataSourceAccounts?.find(
                                                    (account) =>
                                                        account.type ===
                                                        caregiver.source_system,
                                                );

                                            if (!dataSourceAccount) {
                                                return null;
                                            }

                                            const externalUrl =
                                                generateExternalCaregiverUrl(
                                                    caregiver.source_system,
                                                    caregiver.external_id,
                                                    dataSourceAccount.base_url,
                                                );
                                            const systemName =
                                                getDataSourceDisplayName(
                                                    caregiver.source_system,
                                                );
                                            const supportsSingleSync =
                                                (caregiver.source_system ===
                                                    'wellsky' ||
                                                    caregiver.source_system ===
                                                        'axiscare' ||
                                                    caregiver.source_system ===
                                                        'hhaexchange') &&
                                                dataSourceAccount.sync_enabled;

                                            return (
                                                <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                                                    {supportsSingleSync && (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={
                                                                handleSyncCaregiver
                                                            }
                                                            disabled={
                                                                isSyncing ||
                                                                syncCooldownSecondsRemaining >
                                                                    0
                                                            }
                                                            className="flex items-center gap-2"
                                                        >
                                                            {isSyncing ? (
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                            ) : (
                                                                <RefreshCw className="h-4 w-4" />
                                                            )}
                                                            {isSyncing
                                                                ? 'Syncing…'
                                                                : syncCooldownSecondsRemaining >
                                                                    0
                                                                  ? `Available in ${syncCooldownSecondsRemaining}s`
                                                                  : 'Sync'}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        asChild={true}
                                                    >
                                                        <a
                                                            href={externalUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-2"
                                                        >
                                                            <ExternalLink className="h-4 w-4" />
                                                            View in {systemName}
                                                        </a>
                                                    </Button>
                                                </div>
                                            );
                                        })()}
                                </div>
                            </DialogHeader>
                            {shellContent}
                        </React.Fragment>
                    ) : (
                        <div className="px-6 py-8 text-center">
                            <p className="text-muted-foreground">
                                Caregiver not found
                            </p>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            <CallDetailDialog
                organizationId={organizationId}
                voiceCallId={selectedOutcomeVoiceCallId}
                open={isOpen && selectedOutcomeVoiceCallId !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setSelectedOutcomeVoiceCallId(null);
                    }
                }}
                timezone={organization?.timezone ?? 'UTC'}
            />
        </React.Fragment>
    );
}
